import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { EventTypeCreate, Schedule } from '@/client/types.gen';
import { getSchedules } from '@/client/sdk.gen';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const appointmentTypeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  description: z.string().optional(),
  duration: z.string().min(1, 'Duration is required'),
  scheduleId: z.string().optional(),
});

type AppointmentTypeFormValues = z.infer<typeof appointmentTypeSchema>;

interface EventTypeCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateAndEdit: (data: EventTypeCreate) => void;
  onCreateOnly: (data: EventTypeCreate) => void;
}

export function EventTypeCreateModal({
  isOpen,
  onClose,
  onCreateAndEdit,
  onCreateOnly,
}: EventTypeCreateModalProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

  const form = useForm<AppointmentTypeFormValues>({
    resolver: zodResolver(appointmentTypeSchema),
    defaultValues: {
      name: '',
      description: '',
      duration: '30',
      scheduleId: '',
    },
    mode: 'onChange', // Enable real-time validation
  });

  // Fetch schedules when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSchedules();
    }
  }, [isOpen]);

  const fetchSchedules = async () => {
    setIsLoadingSchedules(true);
    try {
      const response = await getSchedules();
      if (response.data) {
        setSchedules(response.data.schedules);

        // If there's only one schedule, select it
        if (response.data.schedules.length === 1) {
          form.setValue('scheduleId', response.data.schedules[0].id);
        }
        // If there are multiple schedules, find and select the default one
        else if (response.data.schedules.length > 1) {
          const defaultSchedule = response.data.schedules.find(
            (s) => s.default_schedule
          );
          if (defaultSchedule) {
            form.setValue('scheduleId', defaultSchedule.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      toast({
        title: 'Error',
        description: 'Failed to load schedules',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      form.reset({
        name: '',
        description: '',
        duration: '30',
        scheduleId: '',
      });
    }
  }, [isOpen, form]);

  const handleSubmit = async (
    data: AppointmentTypeFormValues,
    isEdit: boolean
  ) => {
    // Validate form before submitting
    const result = await form.trigger();
    if (!result) {
      return; // Stop if validation fails
    }

    // Convert form data to API format
    const apiData: EventTypeCreate = {
      name: data.name,
      description: data.description || null,
      duration: parseInt(data.duration),
      schedule_id: data.scheduleId || null,
      // Backend will set ONE_ON_ONE as default and handle hosts
    };

    setIsSubmitting(true);
    try {
      if (isEdit) {
        await onCreateAndEdit(apiData);
      } else {
        await onCreateOnly(apiData);
        form.reset({
          name: '',
          description: '',
          duration: '30',
          scheduleId: '',
        });
      }
    } catch (error) {
      console.error('Failed to create appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to create appointment type',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Appointment Type</DialogTitle>
          <DialogDescription>
            Add the basic details for your new appointment type. You can
            customize more settings later.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Appointment Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Initial Consultation" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief description of the appointment"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {schedules.length > 1 && (
              <FormField
                control={form.control}
                name="scheduleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Availability Schedule</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          {isLoadingSchedules ? (
                            <div className="flex items-center">
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              <span>Loading schedules...</span>
                            </div>
                          ) : (
                            <SelectValue placeholder="Select a schedule" />
                          )}
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {schedules.map((schedule) => (
                          <SelectItem key={schedule.id} value={schedule.id}>
                            {schedule.name}
                            {schedule.default_schedule && ' (Default)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </form>
        </Form>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit(form.getValues(), false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create'
            )}
          </Button>
          <Button
            onClick={() => handleSubmit(form.getValues(), true)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create & Edit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
